import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EllipsisVerticalIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { Dialog, DialogPanel, DialogTitle, Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import type { Customer } from '../context/ManagementContext';
import managementService from '../api/managementService';
import AdvancedEntitySelector from './AdvancedEntitySelector';
import DataTable from './DataTable';

const customerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
});

type CustomerFormData = z.infer<typeof customerSchema>;

export default function CustomerManagement() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  
  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  
  const [isParentSelectorOpen, setIsParentSelectorOpen] = useState(false);
  const [selectedParentForForm, setSelectedParentForForm] = useState<Customer | null>(null);
  const queryClient = useQueryClient();

  // Function to update URL with current filter state
  const updateUrlParams = useCallback(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (currentPage > 1) params.set('page', currentPage.toString());
    setSearchParams(params, { replace: true });
  }, [searchTerm, currentPage, setSearchParams]);


  // Update URL parameters whenever filter state changes
  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  const { data: customersData, isLoading, error } = useQuery({
    queryKey: ['customers', currentPage, searchTerm],
    queryFn: () => managementService.customer.getAll({ 
      page: currentPage, 
      limit: 10,
      includeInactive: true,
      search: searchTerm.trim() || undefined
    }),
  });

  const { data: allCustomers = [] } = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => managementService.customer.getAll({ limit: 100 }),
    select: (data) => data.data,
  });

  const createMutation = useMutation({
    mutationFn: (data: CustomerFormData) => managementService.customer.create({
      ...data,
      isActive: true,
      isDeleted: false,
    } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-all'] });
      queryClient.invalidateQueries({ queryKey: ['customer-selector'] });
      queryClient.invalidateQueries({ queryKey: ['customers-selector-filter'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
      setIsCreateOpen(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CustomerFormData }) =>
      managementService.customer.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-all'] });
      queryClient.invalidateQueries({ queryKey: ['customer-selector'] });
      queryClient.invalidateQueries({ queryKey: ['customers-selector-filter'] });
      setIsEditOpen(false);
      setSelectedCustomer(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => managementService.customer.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-all'] });
      queryClient.invalidateQueries({ queryKey: ['customer-selector'] });
      queryClient.invalidateQueries({ queryKey: ['customers-selector-filter'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => managementService.customer.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-all'] });
      queryClient.invalidateQueries({ queryKey: ['customer-selector'] });
      queryClient.invalidateQueries({ queryKey: ['customers-selector-filter'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => managementService.customer.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      queryClient.invalidateQueries({ queryKey: ['customers-all'] });
      queryClient.invalidateQueries({ queryKey: ['customer-selector'] });
      queryClient.invalidateQueries({ queryKey: ['customers-selector-filter'] });
      queryClient.invalidateQueries({ queryKey: ['statistics'] });
    },
  });

  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm<CustomerFormData>({
    resolver: zodResolver(customerSchema),
  });

  const resetForm = () => {
    reset();
    setSelectedCustomer(null);
    setSelectedParentForForm(null);
  };

  const handleCreate = (data: CustomerFormData) => {
    const formData = {
      ...data,
      parentId: selectedParentForForm?.id || undefined,
    };
    createMutation.mutate(formData);
  };

  const handleEdit = (customer: Customer) => {
    setSelectedCustomer(customer);
    setValue('name', customer.name);
    setValue('description', customer.description || '');
    
    // Set selected parent for the form
    const selectedParent = allCustomers.find(c => c.id === customer.parentId);
    setSelectedParentForForm(selectedParent || null);
    
    setIsEditOpen(true);
  };

  const handleUpdate = (data: CustomerFormData) => {
    if (selectedCustomer) {
      const formData = {
        ...data,
        parentId: selectedParentForForm?.id || undefined,
      };
      updateMutation.mutate({ id: selectedCustomer.id, data: formData });
    }
  };

  const handleDelete = (customer: Customer) => {
    if (window.confirm(t('customer.confirmDelete', { name: customer.name }))) {
      deleteMutation.mutate(customer.id);
    }
  };

  const handleToggleActive = (customer: Customer) => {
    if (customer.isActive) {
      deactivateMutation.mutate(customer.id);
    } else {
      activateMutation.mutate(customer.id);
    }
  };

  const customerColumns = [
    {
      key: 'customer',
      header: t('customer.customer'),
      width: '280px',
      render: (customer: Customer) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 mr-3">
            <div className={`h-2 w-2 rounded-full ${customer.isActive ? 'bg-green-400' : 'bg-gray-300'}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div 
              className="text-sm font-medium text-gray-900 truncate cursor-pointer hover:text-purple-600 transition-colors text-left"
              onClick={() => handleEdit(customer)}
            >
              {customer.name}
            </div>
            <div className="text-xs text-gray-500 flex items-center">
              <BuildingOfficeIcon className="h-3 w-3 mr-1" />
              <span className="truncate">{customer.description || 'No description'}</span>
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'parent',
      header: t('customer.parent'),
      render: (customer: Customer) => (
        <div className="text-sm text-gray-600 truncate max-w-xs">
          {customer.parent?.name || '—'}
        </div>
      )
    },
    {
      key: 'lastUpdated',
      header: t('common.lastUpdated'),
      width: '100px',
      render: (customer: Customer) => (
        <div className="text-xs text-gray-500">
          {new Date(customer.updatedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: '2-digit'
          })}
        </div>
      )
    },
    {
      key: 'actions',
      header: '',
      sticky: 'right' as const,
      width: '60px',
      render: (customer: Customer) => (
        <div className="flex items-center justify-end">
          <Menu as="div" className="relative z-50">
            <MenuButton className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <EllipsisVerticalIcon className="h-5 w-5" />
            </MenuButton>
            <MenuItems anchor="bottom end" className="w-48 bg-white rounded-md shadow-xl border border-gray-200 py-1 z-[9999] focus:outline-none">
              <MenuItem>
                <button
                  onClick={() => handleEdit(customer)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center data-[focus]:bg-gray-100"
                >
                  <PencilIcon className="h-4 w-4 mr-3 text-gray-400" />
                  Edit
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  onClick={() => handleToggleActive(customer)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center data-[focus]:bg-gray-100 ${
                    customer.isActive ? 'text-orange-600 data-[focus]:bg-orange-50' : 'text-green-600 data-[focus]:bg-green-50'
                  }`}
                >
                  {customer.isActive ? (
                    <>
                      <XCircleIcon className="h-4 w-4 mr-3 text-orange-400" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-3 text-green-400" />
                      Activate
                    </>
                  )}
                </button>
              </MenuItem>
              <hr className="my-1" />
              <MenuItem>
                <button
                  onClick={() => handleDelete(customer)}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center data-[focus]:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4 mr-3 text-red-400" />
                  Delete
                </button>
              </MenuItem>
            </MenuItems>
          </Menu>
        </div>
      )
    }
  ];

  // No client-side filtering needed since we're using server-side filtering
  const customers = customersData?.data || [];


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('customer.title')}</h1>
        <button
          onClick={() => setIsCreateOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          {t('customer.addCustomer')}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={t('customer.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="text-sm text-gray-500">
              {t('customer.totalCustomers', { count: customersData?.pagination?.total || 0 })}
            </div>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <DataTable
        data={customers}
        columns={customerColumns}
        isLoading={isLoading}
        error={error}
        pagination={customersData?.pagination}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        emptyStateIcon={BuildingOfficeIcon}
        emptyStateTitle="No customers found"
        emptyStateDescription="Create a new customer to get started"
      />


      {/* Create Customer Dialog */}
      <Dialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-4xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Create New Customer
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                Add a new customer to your organization
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(handleCreate)} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter customer name"
                    />
                    {errors.name && (
                      <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parent Customer
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsParentSelectorOpen(true)}
                      className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                    >
                      {selectedParentForForm ? (
                        <div className="flex items-center space-x-3">
                          <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                          <div>
                            <span className="font-medium text-gray-900">{selectedParentForForm.name}</span>
                            <span className="block text-sm text-gray-500">{selectedParentForForm.description || 'No description'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3 text-gray-500">
                          <BuildingOfficeIcon className="h-5 w-5" />
                          <span>No parent (top-level customer)</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    rows={4}
                    {...register('description')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter customer description"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsCreateOpen(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending}
                    className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {createMutation.isPending ? 'Creating Customer...' : 'Create Customer'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)} className="relative z-50">
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-4xl rounded-xl bg-white shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
              <DialogTitle className="text-xl font-semibold text-gray-900">
                Edit Customer
              </DialogTitle>
              <p className="mt-1 text-sm text-gray-600">
                Update customer information and hierarchy
              </p>
            </div>

            {/* Form */}
            <div className="px-8 py-6">
              <form onSubmit={handleSubmit(handleUpdate)} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customer ID
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={selectedCustomer?.id || ''}
                        readOnly
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-text select-all"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedCustomer?.id) {
                            navigator.clipboard.writeText(selectedCustomer.id);
                            // You could add a toast notification here
                          }
                        }}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                        title="Copy customer ID"
                      >
                        <ClipboardDocumentIcon className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Read-only identifier for this customer</p>
                  </div>
                  <div>
                    <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700 mb-2">
                      Customer Name *
                    </label>
                    <input
                      type="text"
                      id="edit-name"
                      {...register('name')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter customer name"
                    />
                    {errors.name && (
                      <p className="mt-2 text-sm text-red-600">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Parent Customer
                    </label>
                    <button
                      type="button"
                      onClick={() => setIsParentSelectorOpen(true)}
                      className="w-full px-4 py-3 text-left border border-gray-300 rounded-lg hover:border-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                    >
                      {selectedParentForForm ? (
                        <div className="flex items-center space-x-3">
                          <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                          <div>
                            <span className="font-medium text-gray-900">{selectedParentForForm.name}</span>
                            <span className="block text-sm text-gray-500">{selectedParentForForm.description || 'No description'}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3 text-gray-500">
                          <BuildingOfficeIcon className="h-5 w-5" />
                          <span>No parent (top-level customer)</span>
                        </div>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    id="edit-description"
                    rows={4}
                    {...register('description')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="Enter customer description"
                  />
                </div>

                {/* Form Actions */}
                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsEditOpen(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={updateMutation.isPending}
                    className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {updateMutation.isPending ? 'Updating Customer...' : 'Update Customer'}
                  </button>
                </div>
              </form>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {/* Advanced Entity Selector */}
      <AdvancedEntitySelector
        isOpen={isParentSelectorOpen}
        onClose={() => setIsParentSelectorOpen(false)}
        onSelect={(entity) => {
          setSelectedParentForForm(entity as Customer);
        }}
        entityType="customer"
        title="Select Parent Customer"
        selectedId={selectedParentForForm?.id}
      />
    </div>
  );
}